import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Send, Clock, Globe } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success('Message sent successfully! We\'ll get back to you soon.');
    setFormData({ name: '', email: '', company: '', subject: '', message: '' });
    setIsSubmitting(false);
  };

  const contactInfo = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Phone',
      details: [
        { label: 'Netherlands', value: '+31 626 195 070', href: 'tel:+31626195070' },
        { label: 'Lithuania', value: '+370 614 196 46', href: 'tel:+37061419646' }
      ]
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email',
      details: [
        { label: 'General Inquiries', value: 'info@peabsmart.com', href: 'mailto:info@peabsmart.com' }
      ]
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Location',
      details: [
        { label: 'Headquarters', value: 'Netherlands & Lithuania, EU' }
      ]
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Business Hours',
      details: [
        { label: 'Mon - Fri', value: '9:00 AM - 6:00 PM CET' },
        { label: 'Sat - Sun', value: 'Closed' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#0F172A] to-[#1E293B]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Get in Touch
          </h1>
          <p className="text-xl text-[#94A3B8] max-w-2xl mx-auto leading-relaxed">
            Have questions about our DevOps training programs? We're here to help you start your journey to becoming a DevOps professional.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((item, idx) => (
              <div 
                key={idx}
                className="bg-white p-6 rounded-sm border border-[#E2E8F0] hover:border-[#0EA5E9] transition-colors"
                data-testid={`contact-card-${item.title.toLowerCase()}`}
              >
                <div className="w-12 h-12 bg-[#0EA5E9]/10 rounded-sm flex items-center justify-center text-[#0EA5E9] mb-4">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold text-[#1E293B] mb-3">{item.title}</h3>
                <div className="space-y-2">
                  {item.details.map((detail, i) => (
                    <div key={i}>
                      <p className="text-[13px] text-[#64748B]">{detail.label}</p>
                      {detail.href ? (
                        <a 
                          href={detail.href}
                          className="text-[15px] text-[#1E293B] hover:text-[#0EA5E9] transition-colors font-medium"
                        >
                          {detail.value}
                        </a>
                      ) : (
                        <p className="text-[15px] text-[#1E293B] font-medium">{detail.value}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-[#1E293B] mb-4">Send Us a Message</h2>
              <p className="text-[#64748B] mb-8 leading-relaxed">
                Fill out the form below and our team will get back to you within 24 hours.
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-[14px] font-medium text-[#1E293B] mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-sm focus:outline-none focus:border-[#0EA5E9] text-[#1E293B] placeholder-[#94A3B8]"
                      placeholder="John Doe"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-[14px] font-medium text-[#1E293B] mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-sm focus:outline-none focus:border-[#0EA5E9] text-[#1E293B] placeholder-[#94A3B8]"
                      placeholder="john@company.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="company" className="block text-[14px] font-medium text-[#1E293B] mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-sm focus:outline-none focus:border-[#0EA5E9] text-[#1E293B] placeholder-[#94A3B8]"
                      placeholder="Your Company"
                      data-testid="contact-company-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-[14px] font-medium text-[#1E293B] mb-2">
                      Subject *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-sm focus:outline-none focus:border-[#0EA5E9] text-[#1E293B] bg-white"
                      data-testid="contact-subject-select"
                    >
                      <option value="">Select a subject</option>
                      <option value="course-inquiry">Course Inquiry</option>
                      <option value="corporate-training">Corporate Training</option>
                      <option value="pricing">Pricing Information</option>
                      <option value="partnership">Partnership Opportunity</option>
                      <option value="support">Technical Support</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-[14px] font-medium text-[#1E293B] mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-[#E2E8F0] rounded-sm focus:outline-none focus:border-[#0EA5E9] text-[#1E293B] placeholder-[#94A3B8] resize-none"
                    placeholder="Tell us about your training needs..."
                    data-testid="contact-message-textarea"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#0EA5E9] text-white font-semibold rounded-sm hover:bg-[#0284C7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Connect Section */}
            <div>
              <h2 className="text-3xl font-bold text-[#1E293B] mb-4">Connect With Us</h2>
              <p className="text-[#64748B] mb-8 leading-relaxed">
                Follow us on social media for the latest DevOps insights, training updates, and industry news.
              </p>
              
              {/* LinkedIn Card */}
              <div className="bg-[#F8FAFC] p-8 rounded-sm border border-[#E2E8F0] mb-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-[#0A66C2] rounded-sm flex items-center justify-center">
                    <Linkedin className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#1E293B]">LinkedIn</h3>
                    <p className="text-[#64748B]">Follow Peabsmart Consulting</p>
                  </div>
                </div>
                <p className="text-[#475569] mb-6 leading-relaxed">
                  Stay updated with the latest DevOps trends, training announcements, and career opportunities. 
                  Join our growing community of DevOps professionals.
                </p>
                <a
                  href="https://www.linkedin.com/company/peabsmart-consulting/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0A66C2] text-white font-medium rounded-sm hover:bg-[#084d93] transition-colors w-full"
                  data-testid="linkedin-follow-button"
                >
                  <Linkedin className="w-5 h-5" />
                  Follow on LinkedIn
                </a>
              </div>

              {/* Global Reach */}
              <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] p-8 rounded-sm text-white">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-[#0EA5E9]/20 rounded-sm flex items-center justify-center">
                    <Globe className="w-7 h-7 text-[#0EA5E9]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">Global Reach</h3>
                    <p className="text-[#94A3B8]">Training across Europe and beyond</p>
                  </div>
                </div>
                <p className="text-[#94A3B8] leading-relaxed mb-4">
                  With offices in the Netherlands and Lithuania, we deliver DevOps training to organizations across the European Union and internationally.
                </p>
                <ul className="space-y-2 text-[#94A3B8]">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#0EA5E9] rounded-full"></div>
                    On-site corporate training
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#0EA5E9] rounded-full"></div>
                    Virtual instructor-led sessions
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#0EA5E9] rounded-full"></div>
                    Self-paced online courses
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#0EA5E9]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Transform Your Team?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Explore our comprehensive DevOps training programs and start building enterprise-ready skills today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/courses"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#0EA5E9] font-semibold rounded-sm hover:bg-[#F8FAFC] transition-colors"
              data-testid="explore-courses-button"
            >
              Explore Courses
            </Link>
            <Link
              to="/dashboard"
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white font-semibold rounded-sm border-2 border-white hover:bg-white hover:text-[#0EA5E9] transition-colors"
              data-testid="get-started-button"
            >
              Get Started
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
